import axios from 'axios';

// Frontend Analytics Tracking Utility
class AnalyticsTracker {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.isTracking = true;
    this.eventQueue = [];
    this.batchSize = 10;
    this.flushInterval = 30000; // 30 seconds
    
    // Start batch processing
    this.startBatchProcessing();
    
    // Track page visibility for session end detection
    this.setupVisibilityTracking();
  }

  generateSessionId() {
    return `askme_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Get current session ID
  getSessionId() {
    return this.sessionId;
  }

  // Generate a new session ID (for new sessions)
  renewSession() {
    this.sessionId = this.generateSessionId();
    return this.sessionId;
  }

  // Track events with batching
  async trackEvent(eventType, eventData = {}) {
    if (!this.isTracking) return;

    const event = {
      eventType,
      eventData: {
        ...eventData,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        connectionType: this.getConnectionType()
      }
    };

    // Add to queue
    this.eventQueue.push(event);

    // Flush if batch size reached
    if (this.eventQueue.length >= this.batchSize) {
      await this.flush();
    }
  }

  // Get network connection type
  getConnectionType() {
    if ('connection' in navigator) {
      return navigator.connection.effectiveType || 'unknown';
    }
    return 'unknown';
  }

  // Immediate tracking (bypasses queue)
  async trackEventImmediate(eventType, eventData = {}) {
    if (!this.isTracking) return;

    try {
      const headers = {
        'Content-Type': 'application/json',
        'x-session-id': this.sessionId
      };

      // Add auth token if available
      const token = localStorage.getItem('token');
      if (token) {
        headers['x-access-token'] = token;
      }

      await axios.post('/analytics/track', {
        eventType,
        eventData: {
          ...eventData,
          timestamp: new Date().toISOString(),
          url: window.location.href,
          userAgent: navigator.userAgent,
          screenWidth: window.screen.width,
          screenHeight: window.screen.height,
          connectionType: this.getConnectionType()
        }
      }, { headers });
    } catch (error) {
      console.warn('Analytics tracking failed:', error.message);
    }
  }

  // Flush queued events
  async flush() {
    if (this.eventQueue.length === 0) return;

    const eventsToSend = [...this.eventQueue];
    this.eventQueue = [];

    try {
      const headers = {
        'Content-Type': 'application/json',
        'x-session-id': this.sessionId
      };

      const token = localStorage.getItem('token');
      if (token) {
        headers['x-access-token'] = token;
      }

      // Send events in batch
      for (const event of eventsToSend) {
        await axios.post('/analytics/track', event, { headers });
      }
    } catch (error) {
      console.warn('Analytics batch flush failed:', error.message);
      // Re-queue failed events (up to batch size to prevent infinite growth)
      this.eventQueue = [...eventsToSend.slice(-this.batchSize), ...this.eventQueue];
    }
  }

  // Start automatic batch processing
  startBatchProcessing() {
    setInterval(() => {
      this.flush();
    }, this.flushInterval);
  }

  // Setup page visibility tracking for session end
  setupVisibilityTracking() {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // Map legacy session_pause to supported session_end
        this.trackEventImmediate('session_end', { lifecycle: 'pause' });
      } else {
        // Map legacy session_resume to supported session_start with resume flag
        this.trackEventImmediate('session_start', { lifecycle: 'resume' });
      }
    });

    // Track session end on page unload
    window.addEventListener('beforeunload', () => {
      this.trackEventImmediate('session_end');
      // Force flush remaining events
      this.flush();
    });
  }

  // Specific tracking methods for common events
  trackSessionStart(entryPoint = 'direct') {
    return this.trackEventImmediate('session_start', {
      entryPoint,
      featureUsed: 'session_start'
    });
  }

  trackMessageSent(message, interactionType = 'text') {
    return this.trackEvent('message_sent', {
      messageLength: message.length,
      interactionType,
      featureUsed: interactionType,
      queryCategory: this.classifyQuery(message)
    });
  }

  trackServiceClick(serviceName, serviceUrl) {
    return this.trackEvent('service_click', {
      clickedServiceLink: [serviceName],
      serviceUrl,
      featureUsed: 'service_redirect'
    });
  }

  trackAuthPrompt() {
  // Regularized to funnel.login_prompt_shown for taxonomy consistency
  return this.trackEvent('funnel.login_prompt_shown', {
      promptedToLogin: true
    });
  }

  trackLoginClick() {
  // Regularized to funnel.login_prompt_clicked for taxonomy consistency
  return this.trackEvent('funnel.login_prompt_clicked', {
      clickedLoginFromPrompt: true
    });
  }

  trackRegistration() {
    return this.trackEvent('register_click', {
      registeredAfterPrompt: true
    });
  }

  trackQuotaHit() {
    return this.trackEvent('quota_hit', {
      hitQuotaLimit: true
    });
  }

  trackPricingView() {
    return this.trackEvent('pricing_view', {
      viewedPricing: true
    });
  }

  trackSubscriptionUpgrade() {
    return this.trackEvent('subscription_upgrade', {
      upgradedSubscription: true
    });
  }

  trackVoiceInteraction(duration = 0) {
    return this.trackEvent('voice_interaction', {
      interactionType: 'voice',
      featureUsed: 'voice',
      duration
    });
  }

  trackImageInteraction() {
    return this.trackEvent('image_interaction', {
      interactionType: 'image',
      featureUsed: 'image'
    });
  }

  trackInputAbandonment(inputText) {
    return this.trackEvent('input_abandoned', {
      messageLength: inputText.length,
      abandonedInput: true
    });
  }

  trackError(errorType, errorMessage) {
    return this.trackEventImmediate('error_occurred', {
      errorOccurred: true,
      errorType,
      errorMessage
    });
  }

  trackNewSession() {
    this.renewSession();
    return this.trackEvent('session_created', {
      featureUsed: 'new_session'
    });
  }

  // Simple query classification
  classifyQuery(content) {
    if (!content) return 'unknown';
    
    const contentLower = content.toLowerCase();
    
    if (contentLower.includes('resume') || contentLower.includes('cv')) return 'resume';
    if (contentLower.includes('job') || contentLower.includes('apply') || contentLower.includes('career')) return 'job_search';
    if (contentLower.includes('interview') || contentLower.includes('question')) return 'interview';
    if (contentLower.includes('cover letter')) return 'cover_letter';
    if (contentLower.includes('exam') || contentLower.includes('test') || contentLower.includes('study')) return 'exam';
    if (contentLower.includes('salary') || contentLower.includes('negotiate')) return 'salary';
    
    return 'general';
  }

  // Enable/disable tracking
  setTracking(enabled) {
    this.isTracking = enabled;
  }

  // Get analytics data for debugging
  getDebugInfo() {
    return {
      sessionId: this.sessionId,
      isTracking: this.isTracking,
      queueLength: this.eventQueue.length,
      connectionType: this.getConnectionType()
    };
  }
}

// Create and export singleton instance
const analyticsTracker = new AnalyticsTracker();
export default analyticsTracker;
